import {
  createAdminRole as createRoleApi,
  deleteAdminRole as deleteRoleApi,
  listAdminRole as listRolesApi,
  listAdminPermission as listPermissionsApi,
  replacePermissionsAdminRole as replacePermissionsApi,
  updateAdminRole as updateRoleApi,
} from '@api/generated/sdk.gen';

import type {
  CreateRoleRequest,
  PermissionResponse,
  RoleResponse,
  UpdateRoleRequest,
} from '@api/generated/types.gen';

/**
 * Слой доступа к RBAC-эндпоинтам админки.
 *
 * Изолирует сгенерированные функции SDK, чьи имена выводятся из `operationId`
 * SpringDoc (`list`, `update1`, `delete_`, `list4`) и нестабильны — добавление
 * эндпоинта на бэке перенумеровывает их. Остальной код фичи импортирует
 * семантические обёртки отсюда, поэтому после `npm run gen:api` правки нужны
 * только в этом файле.
 *
 * Клиент настроен на `throwOnError: true` (см. `api/runtime-config.ts`), значит
 * на 4xx/5xx вызовы кидают axios-ошибку — её ловит `onError` в react-query.
 */

/** Загружает все роли (бэк сортирует по имени). Требует право `roles.read`. */
export async function fetchRoles(): Promise<RoleResponse[]> {
  const res = await listRolesApi();
  return res.data ?? [];
}

/** Загружает справочник прав (бэк сортирует по имени). Требует право `permissions.read`. */
export async function fetchPermissions(): Promise<PermissionResponse[]> {
  const res = await listPermissionsApi();
  return res.data ?? [];
}

/** Создаёт роль с именем, описанием и набором прав. Требует право `roles.write`. */
export async function createRole(body: CreateRoleRequest): Promise<RoleResponse> {
  const res = await createRoleApi({ body });
  return res.data ?? {};
}

/** Обновляет имя и описание роли, не затрагивая набор прав. Требует право `roles.write`. */
export async function updateRoleMeta(id: string, body: UpdateRoleRequest): Promise<RoleResponse> {
  const res = await updateRoleApi({ path: { id }, body });
  return res.data ?? {};
}

/** Полностью заменяет набор прав роли указанным (replace, не append). Требует право `roles.write`. */
export async function replaceRolePermissions(
  id: string,
  permissionIds: string[],
): Promise<RoleResponse> {
  const res = await replacePermissionsApi({ path: { id }, body: { permissionIds } });
  return res.data ?? {};
}

/**
 * Удаляет роль. Бэк отвечает 409 `ROLE_IN_USE`, если роль ещё назначена хотя бы
 * одному пользователю. Требует право `roles.write`.
 */
export async function deleteRole(id: string): Promise<void> {
  await deleteRoleApi({ path: { id } });
}
